// pages/W_HR_R340.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import DataGrid, {
  Column,
  Paging,
  FilterRow,
  HeaderFilter,
  SearchPanel,
  Sorting,
  Export, 
} from "devextreme-react/data-grid";
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import { Button } from 'devextreme-react/button';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

const ApiColumns = {
  CUST_CD: 'CUST_CD',
  DEVICE: 'DEVICE',
  LOT_NO: 'LOT_NO',
  LOT_NO_SUB: 'LOT_NO_SUB',
  LOT_NO_DUP: 'LOT_NO_DUP',
  TRNX_TM: 'TRNX_TM',
  TRNX_CD: 'TRNX_CD',
  TRNX_QTY_FROM: 'TRNX_QTY_FROM',
  TRNX_QTY_TO: 'TRNX_QTY_TO',
  QTY_DIFF: 'QTY_DIFF',
  TRNX_OPER_FROM: 'TRNX_OPER_FROM',
  TRNX_OPER_TO: 'TRNX_OPER_TO',
  COMM: 'COMM'
};

export default function W_HR_R340({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState([]);
  const [comboLoading, setComboLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formatDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear().toString();
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const d = date.getDate().toString().padStart(2, "0");
    return `${y}${m}${d}`;
  };

  useEffect(() => {
    (async () => {
      setComboLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API_URL}/api/oracle/cust`);
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setCustList(res.data.data || []);
      } catch (e) {
        setError(`CUST 로드 오류: ${e.message}`);
      } finally {
        setComboLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (custList.length > 0 && user?.role === "CUST") {
      const code = user.username.trim().toUpperCase();
      const found = custList.find(item => item.CODE_CD.trim().toUpperCase() === code);
      if (found) {
        setSelectedCust(found.CODE_CD);
      } else {
        setError(`사용자 코드(${user.username})가 CUST 목록에 없습니다.`);
      }
    }
  }, [custList, user]);

  useEffect(() => {
    if (!selectedCust) {
      setDeviceList([]);
      setSelectedDevice(null);
      return;
    }
    
    (async () => {
      setDeviceLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API_URL}/api/oracle/device`, { params: { custCd: selectedCust } });
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setDeviceList(res.data.data || []);
        setSelectedDevice(null);
      } catch (e) {
        setError(`DEVICE 로드 오류: ${e.message}`);
      } finally {
        setDeviceLoading(false);
      }
    })();
  }, [selectedCust]);

  const fetchData = async () => {
    if (!selectedCust) {
      setError("Please select CUST first.");
      return;
    }
    setLoading(true);
    setError("");
    setData([]);

    try {
      const params = {
        inCustCd: selectedCust,
        inDevice: selectedDevice || '%',
        inDateFrom: formatDate(dateFrom),
        inDateTo: formatDate(dateTo),
        inTrnxCd: '%'
      };
      const res = await axios.get(`${API_URL}/api/oracle/W_HR_R340`, { params });
      if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
      const mappedData = (res.data.data || []).map((row, idx) => ({
        ...row,
        id: `${row.LOT_NO || ''}-${row.LOT_NO_SUB || ''}-${row.LOT_NO_DUP || ''}-${row.TRNX_TM}-${idx}`
      }));
      setData(mappedData);
    } catch (e) {
      const errorMessage = e.response?.data?.retMsg || e.message || "알 수 없는 오류가 발생했습니다.";
      setError(`조회 오류: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("PKG_LOT_History");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), "PKG_Lot_History.xlsx");
      });
    });
    e.cancel = true;
  };

  return (
    <div style={{ padding: 0, fontFamily: 'Arial, sans-serif', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>LOT HISTORY TRNX</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 15, alignItems: "center" }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" />
          <DateBox value={dateTo} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateTo(e.value)} label="TO" />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedCust(e.value)} disabled={comboLoading || user?.role === "CUST"} searchEnabled showClearButton={user?.role !== "CUST"} placeholder="Select CUST" label="CUST" />
          <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} disabled={deviceLoading || !selectedCust} searchEnabled showClearButton placeholder="DEVICE (ALL)" label="DEVICE" />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold' }}>{error}</p>}
      </div>

      {/* 2. 그리드 영역 (남은 공간을 모두 차지) */}
      <div style={{ flex: 1, position: 'relative' }}> {/* [수정] position: 'relative' 추가 */}
        {/* 로딩 인디케이터를 그리드 영역 안으로 이동 */}
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading...</div>
          </div>
        )}
        <DataGrid
          dataSource={data}
          keyExpr="id"
          onExporting={onExporting}
          height="100%"
          showBorders={true}
          columnAutoWidth={true}
          allowColumnResizing={true}
          hoverStateEnabled={true}
        >
          <Paging defaultPageSize={10} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} highlightCaseSensitive={false} />
          <Sorting mode="multiple" />
          <Export enabled={true} allowExportSelectedData={false} />

          <Column dataField={ApiColumns.CUST_CD} caption="CUST" alignment="center" />
          <Column dataField={ApiColumns.DEVICE} caption="DEVICE" alignment="center" />
          <Column dataField={ApiColumns.LOT_NO} caption="LOT NO" />
          <Column dataField={ApiColumns.LOT_NO_SUB} caption="SUB" alignment="center" />
          <Column dataField={ApiColumns.LOT_NO_DUP} caption="DUP" alignment="center" />
          <Column dataField={ApiColumns.TRNX_TM} caption="TRNX TIME" />
          <Column dataField={ApiColumns.TRNX_CD} caption="TRNX CD" alignment="center" />
          <Column dataField={ApiColumns.TRNX_QTY_FROM} caption="QTY FROM" dataType="number" alignment="right" />
          <Column dataField={ApiColumns.TRNX_QTY_TO} caption="QTY TO" dataType="number" alignment="right" />
          <Column dataField={ApiColumns.QTY_DIFF} caption="QTY DIFF" dataType="number" alignment="right" />
          <Column dataField={ApiColumns.TRNX_OPER_FROM} caption="OPER FROM" />
          <Column dataField={ApiColumns.TRNX_OPER_TO} caption="OPER TO" />
          <Column dataField={ApiColumns.COMM} caption="COMMENT" width={200} />
        </DataGrid>
      </div>
    </div>
  );
}